import { fetchBiUsableUploads } from '@euler/functions/bi/fetchBiUsableUploads';
import { BiDatasetCatalog } from '@euler/model/bi/catalog';
import { BiUploadedDatasetView } from '@euler/model/bi/dataset';
import { BiDatasetSection } from '@euler/model/bi/library';
import { BiDatasetRefKind } from '@euler/model/bi/query';
import { BiService } from '@euler/services/bi.service';
import { useServiceFactory } from '@euler/services/factory';
import { useCallback, useEffect, useState } from 'react';

export type BiCatalogsState =
  | { kind: 'loading' }
  | {
      kind: 'ready';
      /** The built-in datasets, with their catalogs. */
      builtin: BiDatasetCatalog[];
      /**
       * Uploaded datasets the caller may use (theirs, then shared); a
       * catalog is read when one is picked. Null when they could not be
       * read: the built-in ones still work.
       */
      uploaded: BiUploadedDatasetView[] | null;
    }
  | { kind: 'failed' };

async function usableUploads(
  biService: BiService,
): Promise<BiUploadedDatasetView[] | null> {
  try {
    return await fetchBiUsableUploads(biService);
  } catch (error) {
    console.error('BI uploaded datasets failed', error);
    return null;
  }
}

/**
 * The datasets the caller may build on, read when the editor opens: the
 * built-in catalogs and the usable uploads, again on every open so a new
 * upload or a revoked grant shows. A failed built-in read waits for
 * `retry`.
 */
export function useBiCatalogs(visible: boolean): {
  state: BiCatalogsState;
  retry: () => void;
} {
  const { biService } = useServiceFactory();
  const [state, setState] = useState<BiCatalogsState>({ kind: 'loading' });
  useEffect(() => {
    if (!visible) setState({ kind: 'loading' });
  }, [visible]);
  useEffect(() => {
    if (!visible || state.kind !== 'loading') return;
    let active = true;
    Promise.all([
      biService.listDatasets({ section: BiDatasetSection.Builtin }),
      usableUploads(biService),
    ])
      .then(([list, uploaded]) => {
        if (active)
          setState({
            kind: 'ready',
            // The built-in section lists SQL datasets only.
            builtin: list.datasets.flatMap(entry =>
              entry.kind === BiDatasetRefKind.Sql ? [entry.catalog] : [],
            ),
            uploaded,
          });
      })
      .catch(error => {
        console.error('BI dataset catalogs failed', error);
        if (active) setState({ kind: 'failed' });
      });
    return () => {
      active = false;
    };
  }, [biService, visible, state.kind]);
  const retry = useCallback(() => setState({ kind: 'loading' }), []);
  return { state, retry };
}
